import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useI18n } from '../App';

export default function Hero() {
  const { t, lang, isRTL } = useI18n();
  const h = t.hero;

  const seoTitle = lang === 'ar'
    ? 'Sityx | استضافة سحابية NVMe + تطوير برمجيات بالذكاء الاصطناعي'
    : 'Sityx | Ultra-Fast NVMe Cloud Hosting + AI Software Development';
  const seoDesc = lang === 'ar'
    ? 'استضافة سحابية فائقة السرعة مدعومة بالذكاء الاصطناعي، تطوير مواقع، تطبيقات موبايل، وبرمجيات مخصصة.'
    : 'Ultra-fast NVMe cloud hosting powered by AI, web development, mobile apps, and custom software solutions.';

  return (
    <>
      {/* ── SEO: injected into <head> by react-helmet-async ── */}
      <Helmet>
        <html lang={lang} dir={isRTL ? 'rtl' : 'ltr'} />
        <title>{seoTitle}</title>
        <meta name="description"        content={seoDesc} />
        <meta property="og:title"       content={seoTitle} />
        <meta property="og:description" content={seoDesc} />
        <meta property="og:locale"      content={lang === 'ar' ? 'ar_SY' : 'en_US'} />
        <link rel="canonical"           href="https://sityx.com/" />
      </Helmet>

      <section
        id="hero"
        aria-labelledby="hero-heading"
        className="relative flex items-center justify-center overflow-hidden"
        style={{ minHeight:'100vh', padding:'clamp(80px,12vw,160px) clamp(16px,5vw,80px) clamp(60px,8vw,100px)' }}
      >
        {/* Radial glow */}
        <div className="absolute inset-0 pointer-events-none" style={{
          background:`
            radial-gradient(ellipse 80% 60% at 50% 0%,  rgba(0,229,255,0.08)  0%, transparent 70%),
            radial-gradient(ellipse 60% 50% at 80% 80%, rgba(124,77,255,0.07) 0%, transparent 60%),
            radial-gradient(ellipse 40% 40% at 10% 60%, rgba(0,255,157,0.05)  0%, transparent 50%)`,
        }} />
        <div className="hero-grid-bg pointer-events-none" aria-hidden="true" />

        <div className="relative z-10 text-center max-w-4xl mx-auto w-full">

          {/* Badge */}
          <p
            className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-medium tracking-widest mb-7 uppercase"
            style={{ background:'var(--surface)', border:'1px solid var(--border)', color:'var(--accent-cyan)', animation:'fadeUp 0.6s ease both' }}
          >
            <span className="block w-[6px] h-[6px] rounded-full flex-shrink-0"
              style={{ background:'var(--accent-cyan)', animation:'pulseDot 2s ease infinite' }} />
            {h.badge}
          </p>

          {/* H1 — the only H1 on the page, properly visible in HTML source */}
          <h1
            id="hero-heading"
            className="font-display font-semibold leading-snug mb-6"
            style={{ fontSize:'clamp(1.75rem, 3.8vw, 3rem)', animation:'fadeUp 0.7s 0.1s ease both' }}
          >
            <span className="block text-white">{h.title1}</span>
            <span className="block gradient-text">{h.title2}</span>
          </h1>

          {/* Subtitle */}
          <p
            className="text-sm md:text-base max-w-xl mx-auto mb-11 font-normal"
            style={{ color:'var(--muted)', lineHeight:1.8, animation:'fadeUp 0.7s 0.2s ease both' }}
          >
            {h.subtitle}
          </p>

          {/* CTAs */}
          <div className="flex flex-wrap gap-4 justify-center" style={{ animation:'fadeUp 0.7s 0.3s ease both' }}>
            <a href="#hosting"
              className="inline-flex items-center gap-2 font-medium text-sm px-7 py-3 rounded-xl transition-all duration-200 hover:-translate-y-1 hover:scale-[1.02] glow-card"
              style={{ background:'linear-gradient(135deg,var(--accent-cyan),var(--accent-purple))', color:'#000' }}
            >
              {h.cta1} <span aria-hidden="true">→</span>
            </a>
            <a href="https://wa.me/963983566739" target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-2 font-medium text-sm px-7 py-3 rounded-xl border transition-all duration-200 hover:-translate-y-1"
              style={{ border:'1px solid var(--border)', color:'var(--text)' }}
              onMouseEnter={e=>{ e.currentTarget.style.borderColor='var(--accent-cyan)'; e.currentTarget.style.background='var(--surface)'; }}
              onMouseLeave={e=>{ e.currentTarget.style.borderColor='var(--border)'; e.currentTarget.style.background='transparent'; }}
            >
              {h.cta2}
            </a>
          </div>

          {/* Stats */}
          <div
            className="flex flex-wrap justify-center gap-10 md:gap-16 mt-16 pt-10"
            style={{ borderTop:'1px solid var(--border)', animation:'fadeUp 0.7s 0.45s ease both' }}
          >
            {t.stats.map(({ value, label }) => (
              <div key={label} className="text-center">
                <span className="block font-display font-semibold text-3xl gradient-text">{value}</span>
                <span className="block text-xs mt-1 tracking-wide font-normal" style={{ color:'var(--muted)' }}>{label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
